import React, { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import {
  Box,
  CircularProgress,
  Typography,
  DialogActions,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

import { showSuccessNotification, showErrorNotification } from 'src/components/common/Notifications';

import { ReactComponent as NotificationBellIcon } from '@assets/svg/icons/bell-notification.svg';

import { onSetCalendar } from 'src/pages/MyCompanies/MyProfile/Notifications/NotificationSetup/helpers';

import { handleRequestError } from 'src/utils/handleRequesterror';
import { CalendarSync } from 'src/api/requests';
import { useFetch } from 'src/hooks/useFetch';
import { getUser } from 'src/store/thunks/auth';
import AuthPopup from './AuthPopup/AuthPopup';

import { useStyles } from './styles';

const CalendarSyncModal = ({ open, onClose, orderId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();
  const [step, setStep] = useState(0);
  const [authUrl, setAuthUrl] = useState('');
  const [isSyncLoading, setIsSyncLoading] = useState(false);

  const calendar = useSelector(state => state.user?.currentUserData?.calendar);
  const token = useSelector(state => state.user?.currentUserTokens?.jwt);

  const [{ response: availableCalendars, isLoading }, getAvailableCalendars] = useFetch(
    CalendarSync.getAvailableCalendars,
    false,
    {
      isDataUnpacked: false,
    }
  );

  const handleSetCalendar = async value => {
    try {
      const setCalendarResponse = await onSetCalendar(value);
      if (setCalendarResponse.success) {
        showSuccessNotification(setCalendarResponse.data, 4500);
        dispatch(getUser(token));
      }
    } catch (error) {
      handleRequestError(error);
    }
  };

  useEffect(() => {
    if (step === 1 && !calendar) getAvailableCalendars();
  }, [step, calendar, getAvailableCalendars]);

  const handleSyncWithCalendar = async id => {
    try {
      setIsSyncLoading(true);
      const syncCalendarResponse = await CalendarSync.syncEventWithCalendar({ order_id: id });
      showSuccessNotification(syncCalendarResponse.data, 4000);
      onClose();
    } catch (error) {
      if (error.response?.data) {
        // Axios error
        if (error.response?.data?.error_code === 'calendar_access_token') {
          CalendarSync.calendarAuthorize().then(res => {
            setAuthUrl(res.data.authorize_url);
            setStep(1);
            window.focus();
          });
        }
      } else if (error instanceof Error) {
        // General error
        showErrorNotification(error.message, 4500);
      } else {
        showErrorNotification('An unexpected error occurred', 4500);
      }
    } finally {
      setIsSyncLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    setIsSyncLoading(true);
    handleSyncWithCalendar(orderId)
      .then(res => {
        if (res?.success) {
          onClose();
          showSuccessNotification('Successfully synced', 4500);
        }
      })
      .finally(() => {
        setIsSyncLoading(false);
        setStep(0);
      });
  };

  useEffect(() => {
    const handleFocus = event => {
      if (event.data?.status === 'authSuccess') {
        window.focus();
      }
    };

    window.addEventListener('message', handleFocus);

    return () => window.removeEventListener('message', handleFocus);
  }, [onClose]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      aria-labelledby="responsive-dialog-title"
      classes={{ paper: fullScreen ? classes.dialogRoot : '' }}
    >
      {step === 0 && !calendar && (
        <Box style={{ display: 'flex', flexDirection: 'column', height: '220px' }}>
          <DialogTitle id="responsive-dialog-title">Sync</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <DialogContentText>Choose calendar option or Cancel</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Box
              style={{
                width: '400px',
                height: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                padding: '16px',
              }}
            >
              <Button onClick={onClose} color="primary" variant="contained">
                Cancel
              </Button>
              <Button onClick={() => setStep(1)} color="primary" variant="contained">
                Choose calendar
              </Button>
            </Box>
          </DialogActions>
        </Box>
      )}

      {step === 0 && calendar && (
        <Box className={classes.dialogBoxWrapper}>
          <DialogTitle id="responsive-dialog-title">Sync</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Box style={{ textAlign: 'left' }}>
              <Typography variant="h6" style={{ marginBottom: '10px' }}>
                Leave no stone unturned to get paid on time
              </Typography>
              <Typography>
                Calling and speaking with your customer at critical moments is essential to getting paid on time. This
                wizard helps you say the right thing at the right time. All you need to do to help you is sync these
                dates with your own calendar, and we will guide you every step of the way.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Box
              style={{
                width: '500px',
                height: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                padding: '16px',
              }}
            >
              <Button onClick={onClose} color="primary" variant="contained">
                Cancel
              </Button>
              <Button onClick={() => handleSyncWithCalendar(orderId)} color="primary" variant="contained">
                {isSyncLoading ? <CircularProgress style={{ color: 'white' }} size={24} /> : 'Sync calendar'}
              </Button>
            </Box>
          </DialogActions>
        </Box>
      )}

      {step === 1 && !calendar && (
        <>
          {isLoading ? (
            <Box
              style={{
                width: '448px',
                height: '491px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <DialogTitle id="responsive-dialog-title">Choose Calendar</DialogTitle>
              <DialogContent>
                <Box className={classes.setupSectionWrapper}>
                  <Box>
                    <NotificationBellIcon />
                  </Box>
                  <Box className={classes.setupTextWrapper}>
                    <Typography className={classes.setupDescriptionText}>
                      Set up notifications in Google or Outlook Calendar to ensure you don&apos;t miss making the
                      Proactive Service Calls.
                    </Typography>
                    <Typography>All reminders will be added automatically to your calendar.</Typography>
                  </Box>
                  <Box className={classes.buttonsWrapper}>
                    {availableCalendars &&
                      availableCalendars?.data?.map(option => (
                        <Button
                          key={option}
                          variant="contained"
                          color="primary"
                          className={classes.calendarConnectBtn}
                          onClick={() => handleSetCalendar(option)}
                        >
                          {`Connect
                        ${option}
                        Calendar`}
                        </Button>
                      ))}
                  </Box>
                </Box>
              </DialogContent>
            </>
          )}
        </>
      )}

      {step === 1 && calendar && authUrl.length && (
        <Box style={{ display: 'flex', flexDirection: 'column', height: '220px' }}>
          <DialogTitle id="responsive-dialog-title">Sync</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <DialogContentText>
              {isSyncLoading ? 'Syncing events' : `Authorize with your ${calendar} account`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Box
              style={{
                width: '400px',
                height: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                padding: '16px',
              }}
            >
              <AuthPopup authUrl={authUrl} onAuthSuccess={handleAuthSuccess} isLoading={isSyncLoading} />
            </Box>
          </DialogActions>
        </Box>
      )}
      {step === 1 && calendar && !authUrl.length && (
        <Box
          style={{ width: '448px', height: '491px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <CircularProgress />
        </Box>
      )}
    </Dialog>
  );
};

export default CalendarSyncModal;
